import { useNavParams, useSelector } from 'hooks';
import { useState } from 'react';
import { useIsAnyDragActive } from '../../hooks/useIsAnyDragActive';

import { ORANGE, PRIMARY, RED, WHITE } from 'constants/colors';

import dayjs from 'dayjs';
import { brightness } from 'utils/misc';
import {
  getActivityTypeColor,
  getAddressNotFound,
  getHasWarnings
} from '../../utils';

import { Box, Tooltip } from '@mui/material';
import ContextMenuContainer from '../../components/ContextMenu/ContextMenuContainer';
import { EventTooltip } from '../../components/EventTooltip';

import {
  StyledBoxFlexColumnNoGap,
  StyledBoxFlexStartSmallGap
} from 'components/shared/Box/Box.styles';
import { getZoomLevelProps } from '../../components/ActivityCard/activity-zoom-level-props';
import {
  AddressSection,
  NameSection,
  TimeSection
} from '../../components/ActivityCard/components';
import SecondNameSection from '../../components/ActivityCard/components/SecondNameSection';
import { SignalRow } from '../../components/SignalRow';

import type { SxProps } from '@mui/material';
import type { PlanningEntityType } from 'generated';
import type { ElementType } from 'react';
import { ZoomLevel } from '../../components/Header/ZoomLevelMenu.enums';
import { View } from '../../enums';
import type { CopyModeReturn } from '../../hooks/useCopyMode';
import type { Event } from '../../types';

// NOTE: This is the Pragmatic DnD equivalent of ActivityCard.
// It is intentionally free of @dnd-kit dependencies.
// See CardsViewPragmatic/README.md for the full migration strategy.

type CardProps = Omit<
  Event,
  | 'droppable'
  | 'category'
  | 'date'
  | 'end'
  | 'status'
  | 'isSubmittable'
  | 'isApprovable'
  | 'isDeletable'
> & {
  planningEntityId: string;
  planningEntityType: PlanningEntityType;
};

export interface ActivityCardPragmaticProps extends CardProps {
  component?: ElementType<any>;
  copyMode?: CopyModeReturn;
  setDragDisabled?: (disabled: boolean) => void;
  selected: boolean;
  hovered?: boolean;
  setHovered?: (hovered: boolean) => void;
  isTaskList?: boolean;
  sx?: SxProps;
}

export const ActivityCardPragmatic = (props: ActivityCardPragmaticProps) => {
  'use memo';
  const {
    appointmentGrouping,
    id,
    organizationalUnit,
    activity,
    workPlace,
    project,
    employeeType,
    planningEntityType,
    clients,
    copyMode,
    employees,
    component = 'div',
    noteInternal,
    durationInSeconds,
    warnings,
    isAbsent,
    isAssigned,
    isEditable,
    isCreditable,
    isCredit,
    isOpen,
    isRecurring,
    setDragDisabled,
    scheduleStart,
    start,
    selected,
    timeWindow,
    location,
    schedulingLabel,
    sx,
    hovered,
    linkedDutyCode,
    isTaskList = false,
    setHovered
  } = props;
  const {
    startsTooEarly,
    startsTooLate,
    isInSync: isInSyncWithEcd,
    hasOverlap,
    isOverQualified: overQualified,
    isUnderQualified: underQualified,
    excludedEmployeeClientCombination
  } = warnings || {};
  const client = clients?.[0];
  const employee = employees?.[0];

  const address = client?.address
    ? `${client.address.streetName} ${client.address.houseNumber}, ${client.address.postalCode} ${client.address.city}`
    : '';

  const addressNotFound = getAddressNotFound(location, activity);
  const cardTypeColor = getActivityTypeColor(!!client, isAbsent);
  const { isCopyMode, copyId, pasteAppointment } = copyMode ?? {};

  const { params } = useNavParams();
  const { defaultZoomLevel } = useSelector((state) => state.preferences);
  const zoomLevel = defaultZoomLevel ?? ZoomLevel.STANDARD;

  const [contextMenuActive, setContextMenuActive] = useState<boolean>(false);
  // HTML5 DnD captures the pointer and never fires `mouseleave`, so suppress
  // hover tooltip globally during any drag — otherwise it stays stuck.
  // Subscribed only while hovered: the tooltip is gated on `hovered` anyway, so
  // for every other card the answer cannot change what is rendered — and a
  // board-wide subscription re-renders every card inside the drag event, right
  // when the drop feedback is waiting to paint.
  const isAnyDragActive = useIsAnyDragActive(!!hovered);
  const allowToolTip: boolean =
    !!hovered && !contextMenuActive && !isCopyMode && !isAnyDragActive;

  const allowContextMenu = !isCopyMode;

  const enableCardZoomLevels: boolean =
    params?.view === View.Cards ||
    params?.view === View.CardsPragmatic ||
    params?.view === View.Roster ||
    params?.view === View.RosterPragmatic ||
    params?.view === View.Timeline ||
    params?.view === View.TimelinePragmatic;

  const { showAddress, showSignals, signalThreshold, titleLineClamp } =
    getZoomLevelProps(enableCardZoomLevels, zoomLevel);

  const hasWarnings = getHasWarnings({
    startsTooEarly,
    startsTooLate,
    isInSyncWithEcd,
    excludedEmployeeClientCombination,
    isUrgent: !!timeWindow?.isUrgent,
    hasOverlap
  });

  return (
    <Tooltip
      enterNextDelay={500}
      disableHoverListener={!allowToolTip}
      // Survives the clone below because the child Box doesn't set it. Styling
      // and handlers do NOT: Tooltip clones its child with
      // `{...nameOrDescProps, ...other, ...children.props}` (MUI 7.3.2,
      // Tooltip.js:553-556) and neither `sx` nor `onClick` is destructured out of
      // props or composed, so the child's own win wholesale — only `className`
      // is merged and the hover handlers are composed. An `sx`/`onClick` here is
      // therefore dead code; style and handle on the child Box instead. The
      // `[data-drag-active] &[data-selected]` multi-select drag fade this
      // attribute feeds still needs re-adding there — see #2169.
      data-selected={selected ? '' : undefined}
      title={
        allowToolTip && (
          <EventTooltip
            event={props as unknown as Event}
            onMouseOver={() => {
              setHovered?.(false);
            }}
          />
        )
      }
    >
      <Box
        id={id}
        component={component}
        className={`${isAbsent ? 'absent-card ' : ''} __${id}__ __${dayjs(
          start
        ).format('YYYY-MM-DD')}__ activity-card `}
        sx={{
          boxSizing: 'border-box',
          flexShrink: 0,
          ...(isCopyMode &&
            id == copyId && { border: `2px dashed ${PRIMARY}` }),
          bgcolor: selected ? brightness(cardTypeColor, 90) : WHITE,
          overflow: 'hidden',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          borderLeft: isRecurring
            ? `4px solid ${cardTypeColor}`
            : `2px solid ${cardTypeColor}`,
          position: 'relative',
          // `grab` belongs to the draggable wrapper, the only place that knows
          // whether *this* card can be dragged — so only copy mode's paste
          // target claims a cursor here.
          cursor: isCopyMode && (project || workPlace) ? 'pointer' : 'inherit',
          minHeight: zoomLevel !== ZoomLevel.COMPACT ? 50 : 'initial',
          marginBottom: isTaskList && isAbsent ? '8px' : 'initial',
          '&:hover': {
            ...((project || workPlace) &&
              isCopyMode &&
              id !== copyId && { outline: `2px dashed ${ORANGE}` })
          },
          ...(hasWarnings &&
            !selected && { backgroundColor: brightness(RED, 90) }),
          ...(hasWarnings &&
            selected && {
              backgroundImage:
                'linear-gradient(rgba(244,23,16,0.15), rgba(244,23,16,0.15))'
            }),
          ...(sx ? sx : {})
        }}
        onClick={() => {
          if (isCopyMode && (project?.entityId || workPlace?.entityId)) {
            pasteAppointment?.({
              organizationalUnitId: organizationalUnit?.id,
              ...(project?.entityId
                ? { projectEntityId: project?.entityId }
                : {}),
              ...(workPlace?.entityId
                ? { workPlaceEntityId: workPlace?.entityId }
                : {}),
              activityEntityId: activity ? activity?.entityId : undefined,
              startDate: start
            });
          }
        }}
      >
        <StyledBoxFlexStartSmallGap>
          <StyledBoxFlexColumnNoGap>
            <TimeSection
              durationInSeconds={durationInSeconds}
              isAbsent={isAbsent}
              project={project}
              start={start}
            />

            {showSignals && (
              <SignalRow
                schedulingLabel={schedulingLabel}
                addressNotFound={addressNotFound}
                noteInternal={noteInternal}
                excludedEmployeeClientCombination={
                  excludedEmployeeClientCombination
                }
                hasOverlap={hasOverlap}
                isAbsent={isAbsent}
                isInSyncWithEcd={isInSyncWithEcd}
                isRecurring={isRecurring}
                locationType={location?.locationType}
                overQualified={overQualified}
                selected={selected}
                signalThreshold={signalThreshold}
                startsTooEarly={startsTooEarly}
                startsTooLate={startsTooLate}
                timeWindow={timeWindow}
                underQualified={underQualified}
              />
            )}
          </StyledBoxFlexColumnNoGap>

          <StyledBoxFlexStartSmallGap>
            <StyledBoxFlexColumnNoGap>
              <NameSection
                planningEntityType={planningEntityType}
                activity={activity}
                client={client}
                isAbsent={isAbsent}
                titleLineClamp={titleLineClamp}
              />
              <SecondNameSection
                planningEntityType={planningEntityType}
                schedulingLabel={schedulingLabel}
                activity={activity}
                employee={employee}
                client={client}
                employeeType={employeeType}
                isAbsent={isAbsent}
                linkedDutyCode={linkedDutyCode}
              />
            </StyledBoxFlexColumnNoGap>
            {allowContextMenu && (
              <ContextMenuContainer
                eventId={id}
                scheduleStart={scheduleStart}
                clientId={client?.id}
                eventIsOpen={isOpen}
                hasAppointmentGrouping={!!appointmentGrouping}
                hasEmployee={!!employee}
                isAssigned={!!isAssigned}
                isAbsent={isAbsent}
                isEditable={isEditable}
                isCreditable={isCreditable}
                isCredit={isCredit}
                schedulingLabel={schedulingLabel}
                isProject={!!project}
                isWorkPlace={!!workPlace}
                onOpen={(open: boolean) => {
                  setContextMenuActive(open);
                  setDragDisabled?.(open);
                }}
                sx={{ position: 'absolute', top: 0, right: 0 }}
              />
            )}
          </StyledBoxFlexStartSmallGap>
        </StyledBoxFlexStartSmallGap>

        {/* BOTTOM ROW - ADDRESS DETAILS */}
        {showAddress && address && <AddressSection address={address} />}
      </Box>
    </Tooltip>
  );
};
